/**
 * @author Muhammad Hamza Khan
 * @datetime April, 15th 2022
 * @description: This is Remove Floor Unit Component that is responsible for deleting Unit from floors.
 */
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { SiteManagerComponent } from 'src/app/site-management/site-manager/site-manager.component';
import { Subscription, Subject } from 'rxjs';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { FloorManagementService } from '../floor-management.service';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { PageName } from 'src/app/shared/utils/user-roles';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { CommonService } from 'src/app/shared/utils/common.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-remove-floor-unit',
  templateUrl: './remove-floor-unit.component.html',
  styleUrls: ['./remove-floor-unit.component.scss'],
})
export class RemoveFloorUnitComponent implements OnInit, OnDestroy {
  public ngDestroyed$ = new Subject();
  @Input() public selectedSite: any;
  @Input() selectedBuilding: any;
  @Input() public selectedFloor: any;
  @Input() selectedUnit: any;
  @Input() removeAP: boolean | undefined;
  CONSTANTS = CONSTANTS;
  readonly LANG_CONST = LANG_CONST;
  sharedData!: any;
  floorManagemetSubscription: Subscription | undefined;
  unitList: any;
  PageName = PageName;
  subscription: Subscription | undefined;
  result: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<SiteManagerComponent>,
    private floorManagementService: FloorManagementService,
    private sharedDataService: SharedDataService,
    private notificationService: NotificationService,
    private commonService: CommonService,
    private translate: TranslateService
  ) {}

  /**
   * @description: runs on when component is initialized.
   * @input {none}
   * @output {void}
   */

  ngOnInit(): void {
    this.sharedDataService.currentData.subscribe((sharedData: any) => {
      this.sharedData = sharedData;
    });
  }

  /**
   * @description: closeDialog function close the dialogRef.
   * @input none
   * @output { void }
   */

  closeDialog() {
    this.dialogRef.close(this.result);
  }

  deleteItem() {
    if (this.removeAP) {
      this.deleteAPHandler(
        this.selectedSite?.siteId,
        this.selectedBuilding?.buildingId,
        this.selectedFloor?.floorId,
        this.sharedData.selectedAccessPoint?.accessPointId
      );
    } else {
      this.deleteUnitHandler(
        this.selectedSite?.siteId,
        this.selectedBuilding?.buildingId,
        this.selectedFloor?.floorId,
        this.selectedUnit?.unitId
      );
    }
  }

  /**
   * @description: deleteAPHandler function accepts siteId , buildingId , floorId , accessPointId and call the deleteAPHandler API from floorManagementService.
   * @input { string }
   * @param { siteId, buildingId , floorId , accessPointId  }
   * @output { void }
   */

  deleteAPHandler(
    siteId: string,
    buildingId: string,
    floorId: string,
    accessPointId: string
  ) {
    this.floorManagementService
      .deleteAccessPoint(siteId, buildingId, floorId, accessPointId)
      .subscribe({
        next: (res: any) => {
          this.notificationService.showSuccess(
            this.commonService.stringInterPolation(
              this.translate.instant('SUCCESSFULLY_DELETED'),
              this.translate.instant('ACCESS_POINT')
            )
          );

          this.sharedDataService.setSelectedAccessPoint({
            accessPointId: null,
            accessPointName: null,
          });
          this.result = true;
          this.dialogRef.close(this.result);
        },
        error: (err: Error) => {
          this.notificationService.showError(err?.message);
          if (window.__dev__) console.log('Delete Access point API: ', err);
        },
      });
    this.dialogRef.afterClosed().subscribe(() => {
      this.floorManagementService
        .getUnitsByFloor(siteId, buildingId, floorId)
        .subscribe({
          next: (res: any) => {
            this.unitList = res;
          },
          error: (err: Error) => {
            if (window.__dev__) console.log('Get Unit By Floor API: ', err);
          },
        });
    });
  }

  /**
   * @description: deleteUnitHandler function accepts siteId , buildingId , floorId , unitId and call the deleteUnit API from floorManagementService.
   * @input { string }
   * @param { siteId, buildingId , floorId , unitId  }
   * @output { void }
   */

  deleteUnitHandler(
    siteId: string,
    buildingId: string,
    floorId: string,
    unitId: string
  ) {
    this.floorManagementService
      .deleteUnit(siteId, buildingId, floorId, unitId)
      .subscribe({
        next: (res: any) => {
          if (res?.unitId == this?.sharedData?.selectedUnit?.unitId) {
            this.sharedDataService.setSelectedUnit({
              unitId: null,
              unitName: null,
            });
            this.result = true;
            this.dialogRef.close(this.result);
            this.notificationService.showSuccess(
              this.commonService.stringInterPolation(
                this.translate.instant('SUCCESSFULLY_DELETED'),
                this.translate.instant('UNIT')
              )
            );
          }
        },
      });

    this.dialogRef.afterClosed().subscribe(() => {
      this.floorManagementService
        .getUnitsByFloor(siteId, buildingId, floorId)
        .subscribe({
          next: (res: any) => {
            this.unitList = res;
          },
          error: (err: Error) => {
            if (window.__dev__) console.log('Get Unit By Floor API: ', err);
          },
        });
    });
  }
  /**
   * @description: runs just before component unmount. Unsubscribes the sharedDataService service
   * @input {none}
   * @output {void}
   */
  ngOnDestroy(): void {
    this.floorManagemetSubscription?.unsubscribe();
  }
}
